import React, { useState, useEffect } from "react";
import { X, PlusCircle, CircleDollarSign, Tag, ReceiptText, Repeat, ArrowLeftRight } from 'lucide-react';

const EXPENSE_CATEGORIES = [
  "Food", "Transport", "Utilities", "Shopping", "Entertainment", "Healthcare"
];
const INCOME_CATEGORIES = [
  "Salary", "Freelance", "Investments", "Consulting", "Online Sales", "Gifts"
];
const RECURRENCE_OPTIONS = [
  { value: "none", label: "None" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const AddTransactionModal = ({ isOpen, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    amount: "",
    type: "expense",
    recurrence: "none",
    category: EXPENSE_CATEGORIES[0],
    description: "",
  });

useEffect(() => {
  const amt = parseFloat(form.amount);
  if (!isNaN(amt)) {

  }
}, [form.amount]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "type") {
      const newCats = value === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
      setForm((prev) => ({
        ...prev,
        type: value,
        category: newCats[0],
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault(); // Use form's onSubmit for better accessibility
    if (!form.amount || !form.category) return;
    
    // Ensure amount sent is always positive, type defines if it's income/expense
    const absoluteAmount = Math.abs(parseFloat(form.amount));

    onSubmit({
      ...form,
      amount: absoluteAmount,
    });

    // Reset form after submission
    setForm({
      amount: "",
      type: "expense",
      recurrence: "none",
      category: EXPENSE_CATEGORIES[0],
      description: "",
    });
  };

  if (!isOpen) return null;

  const availableCategories = form.type === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;

  const selectArrowStyle = {
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 0.75rem center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '1.5em 1.5em',
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 backdrop-blur-sm p-4">
      <div className="bg-slate-50 p-6 rounded-xl w-full max-w-md shadow-2xl animate-scale-in">
        <div className="flex justify-between items-center mb-4 pb-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <PlusCircle className="text-[#2ecfe3]" size={24} />
            <h2 className="text-xl font-bold text-gray-800">Add Transaction</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* --- Type: Segmented Control --- */}
          <div>
              <label className="text-sm font-medium text-gray-600 mb-2 block">Type</label>
              <div className="grid grid-cols-2 gap-2 bg-slate-200 p-1 rounded-lg">
                  <button type="button" onClick={() => handleChange({ target: { name: 'type', value: 'expense' }})} className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${form.type === 'expense' ? 'bg-white text-[#2ecfe3] shadow' : 'text-gray-600 hover:bg-slate-100'}`}>Expense</button>
                  <button type="button" onClick={() => handleChange({ target: { name: 'type', value: 'income' }})} className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${form.type === 'income' ? 'bg-white text-[#2ecfe3] shadow' : 'text-gray-600 hover:bg-slate-100'}`}>Income</button>
              </div>
          </div>
          
          {/* --- Input Field with Icon: Amount --- */}
          <div>
            <label htmlFor="amount" className="text-sm font-medium text-gray-600 mb-1 block">Amount</label>
            <div className="relative">
              <CircleDollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                id="amount"
                name="amount"
                type="number"
                step="0.01"
                placeholder="e.g. 50.00"
                value={form.amount}
                onChange={handleChange}
                className="w-full bg-white border border-slate-200 pl-10 pr-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2ecfe3] focus:border-[#2ecfe3] transition"
                required
              />
            </div>
          </div>
          
          {/* --- Select Field with Icon: Category --- */}
          <div>
            <label htmlFor="category" className="text-sm font-medium text-gray-600 mb-1 block">Category</label>
            <div className="relative">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <select
                id="category"
                name="category"
                value={form.category}
                onChange={handleChange}
                className="w-full bg-white border border-slate-200 pl-10 pr-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2ecfe3] focus:border-[#2ecfe3] transition appearance-none"
                style={selectArrowStyle}
                required
              >
                {availableCategories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          {/* --- Select Field with Icon: Recurrence --- */}
          <div>
            <label htmlFor="recurrence" className="text-sm font-medium text-gray-600 mb-1 block">Recurrence</label>
            <div className="relative">
              <Repeat className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <select
                id="recurrence"
                name="recurrence"
                value={form.recurrence}
                onChange={handleChange}
                className="w-full bg-white border border-slate-200 pl-10 pr-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2ecfe3] focus:border-[#2ecfe3] transition appearance-none"
                style={selectArrowStyle}
                required
              >
                {RECURRENCE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* --- Textarea Field with Icon: Description --- */}
          <div>
            <label htmlFor="description" className="text-sm font-medium text-gray-600 mb-1 block">Description (optional)</label>
            <div className="relative">
              <ReceiptText className="absolute left-3 top-3.5 text-gray-400" size={20} />
              <textarea
                id="description"
                name="description"
                placeholder="e.g. Dinner with friends"
                value={form.description}
                onChange={handleChange}
                rows={3}
                className="w-full bg-white border border-slate-200 pl-10 pr-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2ecfe3] focus:border-[#2ecfe3] transition resize-none"
              />
            </div>
          </div>

          {/* --- Modernized Action Buttons --- */}
          <div className="mt-8 flex justify-end gap-4 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 rounded-lg text-sm font-semibold text-gray-600 bg-transparent hover:bg-gray-100 border border-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 rounded-lg text-sm font-semibold text-white bg-[#2ecfe3] hover:bg-[#28bfcf] transition-all shadow-sm hover:shadow-md"
            >
              Add Transaction
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTransactionModal;
